import { env } from "../core/env.js";

// Port of src/sentry.ts + src/user/utilities/sentry_utility.tsx.
//
// The SDK is loaded through a dynamic import() so it lands in its own async chunk and never blocks
// first paint, and every entry point is wrapped so a blocked CDN / adblocked chunk / offline client
// degrades to "no error reporting" rather than to a broken app. React used @sentry/react for its
// error boundary + component tracing; nothing here is React-specific, so the framework-agnostic
// @sentry/browser is used instead.
//
// Errors thrown in the gap between boot and the SDK resolving would otherwise be lost, so
// initSentry() installs lightweight window `error`/`unhandledrejection` listeners up front and
// replays what they catch once the SDK loads. setUser calls made before then are remembered and
// applied on load.

// A dynamic import() of a hashed chunk can fail for reasons that are never an app bug and that we
// cannot act on: the client is running a stale index.html whose chunks 404 after a redeploy, or a
// flaky mobile network dropped the module-script fetch. Recovery is already handled —
// @bitcashier/shared/router/staleChunk.js reloads once to pull the fresh entry. Left unfiltered,
// though, a single stale client emits hundreds of identical events (one per failed route-nav),
// drowning real signal, so drop them here. Browser-specific wordings: WebKit/iOS "Importing a
// module script failed", Chromium "Failed to fetch dynamically imported module", Firefox "error
// loading dynamically imported module", plus Vite's CSS-preload failure.
export const IGNORED_DYNAMIC_IMPORT_ERRORS = [
  /Importing a module script failed/i,
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Unable to preload CSS/i,
];

export const isDynamicImportError = (reason) => {
  const message = reason instanceof Error ? reason.message : typeof reason === "string" ? reason : "";
  return IGNORED_DYNAMIC_IMPORT_ERRORS.some((pattern) => pattern.test(message));
};

// No DSN configured (every non-production .env leaves REACT_APP_SENTRY_KEY empty) means the whole
// module is inert: no chunk is fetched, no listeners are installed. React additionally skipped the
// local dev environment, which is kept.
export const sentryEnabled = (key, environment) => Boolean(key) && environment !== "local";

const enabled = sentryEnabled(env.sentryKey, env.environment);

let sentryPromise = null;
let currentUser = null;

const loadSentry = () => {
  if (!sentryPromise) sentryPromise = import("@sentry/browser");
  return sentryPromise;
};

export const initSentry = () => {
  if (!enabled) return;

  const earlyErrors = [];
  const onError = (event) => {
    const error = event.error ?? event.message;
    if (isDynamicImportError(error)) return;
    earlyErrors.push(error);
  };
  const onRejection = (event) => {
    if (isDynamicImportError(event.reason)) return;
    earlyErrors.push(event.reason);
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);

  const removeListeners = () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  };

  loadSentry()
    .then((Sentry) => {
      Sentry.init({
        dsn: env.sentryKey,
        environment: env.environment,
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: 1.0,
        ignoreErrors: IGNORED_DYNAMIC_IMPORT_ERRORS,
      });
      removeListeners();
      earlyErrors.forEach((error) => Sentry.captureException(error));
      if (currentUser) Sentry.setUser(currentUser);
    })
    .catch(removeListeners);
};

// Reports a failure the app HANDLED. Everything else here is about errors that escaped, which the
// window listeners and the SDK pick up on their own — but a boot read that fails is caught, turned
// into a message on screen, and otherwise invisible: nothing counted how often the dashboard
// failed to start, which is why "it happens sometimes, then a reload fixes it" could only ever be
// answered with a guess. `context` becomes searchable tags (the HTTP status above all: a 502 from
// the edge and a 401 from the gateway are the same screen and completely different causes).
// Inert without a DSN, and — like every other entry point here — survives the SDK never loading.
export const reportError = (error, context = {}) => {
  if (!enabled) return;
  loadSentry()
    .then((Sentry) => Sentry.captureException(error, { tags: context }))
    .catch(() => {});
};

// Tags every subsequent event with the client whose account the user is acting on — which is the
// unit support and the Sentry→Slack alert rules triage by, not the Auth0 login.
export const setActiveSentryUser = (clientId, clientName) => {
  if (!enabled) return;
  currentUser = clientId && clientName ? { id: clientId, username: clientName } : null;
  loadSentry()
    .then((Sentry) => Sentry.setUser(currentUser))
    .catch(() => {});
};

export const deleteActiveSentryUser = () => {
  if (!enabled) return;
  currentUser = null;
  loadSentry()
    .then((Sentry) => Sentry.setUser(null))
    .catch(() => {});
};
